import { api } from '@/api/client';
import { appConfig } from '@/config/app';
import type {
  AddVideosToCanvasResponse,
  CanvasAssetResponse,
  CanvasParticipant,
  InviteParticipantPayload,
  JoinCanvasResponse,
  ParticipantsResponse,
  CanvasDocumentResponse,
  CanvasListResponse,
  CanvasResponse,
  ResolveLinkResponse,
  SaveCanvasDocumentPayload,
  SaveCanvasDocumentResponse,
  ShareCanvasPayload,
  ShareCanvasResponse,
  SharedCanvasResponse,
} from '@/api/types';

/** Контракт канвасов v2 (canvas-spec.md): бэкенд S2–S3 и mock. */
const canvasesV2 = {
  list: () => api.get<CanvasListResponse>('/canvases'),

  create: (payload: { name?: string } = {}) => api.post<CanvasResponse>('/canvases', payload),

  rename: (id: number, name: string) => api.patch<CanvasResponse>(`/canvases/${id}`, { name }),

  remove: (id: number) => api.delete<{ ok: boolean }>(`/canvases/${id}`),

  setDefault: (id: number) => api.post<CanvasResponse>(`/canvases/${id}/set-default`, {}),

  /** Из выдачи поиска уходит X-Search-Event-Id (CNV-20). */
  addVideos: (id: number, videoIds: number[], searchEventId?: number | null) =>
    api.post<AddVideosToCanvasResponse>(
      `/canvases/${id}/videos`,
      { video_reference_ids: videoIds },
      searchEventId ? { headers: { 'X-Search-Event-Id': String(searchEventId) } } : undefined,
    ),

  removeVideo: (id: number, videoId: number) =>
    api.delete<CanvasResponse>(`/canvases/${id}/videos/${videoId}`),

  // ——— Полотно (шаг 6). Контракт предложен фронтом; TODO: generate from OpenAPI (NFR-2) ———
  /** Документ полотна: узлы + связи + версия (CNV-3). */
  document: (id: number) => api.get<CanvasDocumentResponse>(`/canvases/${id}/document`),

  /** Автосохранение целиком с optimistic version (CNV-4). */
  saveDocument: (id: number, payload: SaveCanvasDocumentPayload) =>
    api.put<SaveCanvasDocumentResponse>(`/canvases/${id}/document`, payload),

  /** Создать/обновить публичную ссылку (CNV-22). */
  share: (id: number, payload: ShareCanvasPayload) =>
    api.post<ShareCanvasResponse>(`/canvases/${id}/share`, payload),

  revokeShare: (id: number) => api.delete<{ ok: boolean }>(`/canvases/${id}/share`),

  /** Публичный канвас по токену — без Bearer; 410 = истекла/отозвана (CNV-24/25). */
  shared: (token: string) =>
    api.get<SharedCanvasResponse>(`/shared/canvases/${encodeURIComponent(token)}`),

  /** Картинка на канвас → файл в хранилище; узел хранит `asset_id` (canvas-spec.md §5). */
  uploadAsset: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post<CanvasAssetResponse>(`/canvases/${id}/assets`, form);
  },

  // ——— Участники (canvas-spec.md §7) ———
  participants: (id: number) => api.get<ParticipantsResponse>(`/canvases/${id}/participants`),

  /** Приглашение по почте; 403 paid_feature на бесплатном тарифе, 409 already_invited / already_participant. */
  invite: (id: number, payload: InviteParticipantPayload) =>
    api.post<{ data: CanvasParticipant }>(`/canvases/${id}/participants`, payload),

  removeParticipant: (id: number, participantId: number) =>
    api.delete<{ ok: boolean }>(`/canvases/${id}/participants/${participantId}`),

  /** Принять приглашение из письма (/app/invites/:token). */
  acceptInvite: (token: string) =>
    api.post<JoinCanvasResponse>(`/invites/${encodeURIComponent(token)}/accept`, {}),

  /** Войти редактором по edit-ссылке; 403 link_is_view_only. */
  join: (token: string) =>
    api.post<JoinCanvasResponse>(`/shared/canvases/${encodeURIComponent(token)}/join`, {}),

  /** Ссылка на видео → узел (CNV-8). */
  resolveLink: (url: string) => api.post<ResolveLinkResponse>('/canvases/resolve-link', { url }),
};

/**
 * Live ходит в настоящие `/canvases*` бэкенда (срезы S2–S5 canvas-spec.md): документ,
 * список, шаринг с режимом, публичная страница, файлы, ссылка на видео.
 * Участники (S6) тоже на сервере. Флаги возможностей читает интерфейс (Share).
 */
export const canvasesApi =
  appConfig.apiMode === 'mock'
    ? { ...canvasesV2, capabilities: { editLinks: true, participants: true, serverLayout: true } }
    : { ...canvasesV2, capabilities: { editLinks: true, participants: true, serverLayout: true } };
